/**
 * Combined Swatch Picker Component
 * Handles variant selection for products with split Clay/Glaze options
 * by creating a unified swatch interface that matches the original design.
 */

class CombinedSwatchPicker extends HTMLElement {
  constructor() {
    super();
    this.claySelect = null;
    this.glazeSelect = null;
    this.swatchLabels = [];
    this.currentVariantId = null;
    this.displayUpdateTimer = null;
  }

  connectedCallback() {
    this.initializeElements();
    this.bindEvents();
    this.updateActiveSwatch();
  }

  /**
   * Initialize DOM elements
   */
  initializeElements() {
    // Resolve selects from the variant form so Shopify's variant-picker sees changes
    const variantPicker = this.closest('variant-picker');
    const formRoot = (variantPicker && variantPicker.querySelector('form')) || document;
    this.claySelect = formRoot.querySelector('select[name="options[Clay]"]');
    this.glazeSelect = formRoot.querySelector('select[name="options[Glaze]"]');
    
    // Find all swatch labels
    this.swatchLabels = this.querySelectorAll('.combined-swatch-label');
    
    // Find the selected variant text element
    this.selectedVariantText = this.querySelector('.variant-option__selected');
    
    // Get current variant ID from the variant picker
    if (variantPicker) {
      this.currentVariantId = variantPicker.dataset.productId;
    }
  }

  /**
   * Bind event listeners
   */
  bindEvents() {
    this.swatchLabels.forEach(label => {
      label.addEventListener('click', this.handleSwatchClick.bind(this));
      label.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.handleSwatchClick(e);
        }
      });
      // Update selection display on hover
      label.addEventListener('mouseenter', this.handleSwatchHover.bind(this));
      label.addEventListener('mouseleave', this.handleSwatchLeave.bind(this));
    });

    // Listen for changes to the hidden selects (in case they're updated elsewhere)
    if (this.claySelect) {
      this.claySelect.addEventListener('change', this.handleSelectChange.bind(this));
    }
    if (this.glazeSelect) {
      this.glazeSelect.addEventListener('change', this.handleSelectChange.bind(this));
    }
  }

  /**
   * Handle swatch hover
   */
  handleSwatchHover(event) {
    const label = event.currentTarget;
    const clayValue = label.dataset.clay;
    const glazeValue = label.dataset.glaze;
    this.updateSelectionDisplay(clayValue, glazeValue);
  }

  /**
   * Handle swatch leave
   */
  handleSwatchLeave(event) {
    this.restoreSelectionDisplay();
  }

  /**
   * Handle swatch click
   */
  handleSwatchClick(event) {
    event.preventDefault();
    
    const label = event.currentTarget;
    const clayValue = label.dataset.clay;
    const glazeValue = label.dataset.glaze;
    const variantId = label.dataset.variantId;

    // Update the hidden select elements (pass variantId to ensure correct option is selected)
    this.updateSelects(clayValue, glazeValue, variantId);

    // Update active swatch styling
    this.updateActiveSwatch(label);
    
    // Update the selection display
    this.updateSelectionDisplay(clayValue, glazeValue);

    // Trigger variant change event (even for unavailable variants)
    this.triggerVariantChange(variantId);

    // Update URL (even for unavailable variants)
    this.updateUrl(variantId);
  }

  /**
   * Update hidden select elements without triggering change events
   */
  updateSelects(clayValue, glazeValue, variantId) {
    const variantPicker = this.closest('variant-picker');
    
    // Set flag BEFORE updating to prevent filtering logic from running
    if (variantPicker) {
      variantPicker.dataset.skipFiltering = 'true';
    }
    
    // Create listeners to stop change events from bubbling
    const stopEvent = (e) => {
      e.stopImmediatePropagation();
      e.stopPropagation();
    };
    
    // Add one-time stop listeners in capture phase (fires before target listeners)
    if (this.claySelect) {
      this.claySelect.addEventListener('change', stopEvent, { capture: true, once: true });
    }
    if (this.glazeSelect) {
      this.glazeSelect.addEventListener('change', stopEvent, { capture: true, once: true });
    }
    
    // CRITICAL: Always restore all glaze options first if available
    // This ensures we can find the correct option even if filtering has removed it
    const clayChanged = this.claySelect && this.claySelect.value !== clayValue;
    if (this.glazeSelect && this.glazeSelect.dataset.allOptionsHtml) {
      // Restore all options so we can find the correct one by variant ID
      const allOptionsHtml = this.glazeSelect.dataset.allOptionsHtml;
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = allOptionsHtml;
      const allOptions = Array.from(tempDiv.querySelectorAll('option'));
      
      // Clear and restore all options
      this.glazeSelect.innerHTML = '';
      allOptions.forEach(opt => {
        const cloned = opt.cloneNode(true);
        this.glazeSelect.appendChild(cloned);
      });
    }
    
    // Update clay select - change event fires but is stopped from bubbling
    if (this.claySelect) {
      const clayValueBefore = this.claySelect.value;
      this.claySelect.value = clayValue;
      if (clayValueBefore !== clayValue) {
        Array.from(this.claySelect.options).forEach(opt => {
          if (opt.value === clayValue) opt.setAttribute('selected', 'selected'); 
          else opt.removeAttribute('selected');
        });
      }
    }
    
    // Update glaze select - ALWAYS find by variant ID (not by value, since values can overlap)
    if (this.glazeSelect && variantId) {
      // Find the correct option by variant ID ONLY (most reliable, handles overlapping names)
      const correctOption = Array.from(this.glazeSelect.options).find(opt => 
        opt.dataset.variantId === variantId
      );
      
      if (correctOption) {
        // Select the correct option explicitly
        this.glazeSelect.value = correctOption.value;
        // Also ensure it's marked as selected attribute-wise
        Array.from(this.glazeSelect.options).forEach(opt => {
          opt.removeAttribute('selected');
          if (opt === correctOption) {
            opt.setAttribute('selected', 'selected');
          }
        });
        // Force the selectedIndex to match
        this.glazeSelect.selectedIndex = Array.from(this.glazeSelect.options).indexOf(correctOption);
      } else {
        console.warn('Could not find glaze option with variant ID:', variantId);
        // Fallback: try by value (but this might select wrong one if names overlap)
        const fallbackOption = Array.from(this.glazeSelect.options).find(opt => 
          opt.value === glazeValue || (opt.dataset.optionValue && opt.dataset.optionValue === glazeValue)
        );
        if (fallbackOption) {
          this.glazeSelect.value = fallbackOption.value;
          Array.from(this.glazeSelect.options).forEach(opt => opt.removeAttribute('selected'));
          fallbackOption.setAttribute('selected', 'selected');
        }
      }
    }
    
    // Clear flag - we'll trigger change manually
    setTimeout(() => {
      if (variantPicker) {
        delete variantPicker.dataset.skipFiltering;
      }
    }, 0);
  }

  /**
   * Update active swatch styling
   */
  updateActiveSwatch(activeLabel = null) {
    this.swatchLabels.forEach(label => {
      if (activeLabel && label === activeLabel) {
        label.setAttribute('data-selected', 'true');
      } else if (activeLabel) {
        // Another label was explicitly selected, deselect this one
        label.setAttribute('data-selected', 'false');
      } else {
        // No explicit selection, check if this label matches current selection
        const clayValue = label.dataset.clay;
        const glazeValue = label.dataset.glaze;
        const isSelected = this.isCurrentSelection(clayValue, glazeValue);
        label.setAttribute('data-selected', isSelected ? 'true' : 'false');
      }
    });
    
    // Update the selected variant text
    this.updateSelectedVariantText();
  }

  /**
   * Update the selected variant text display
   */
  updateSelectedVariantText() {
    if (!this.claySelect || !this.glazeSelect) return;
    
    const clayValue = this.claySelect.value;
    const glazeValue = this.glazeSelect.value;
    this.updateSelectionDisplay(clayValue, glazeValue);

    if (this.selectedVariantText) {
      this.selectedVariantText.textContent = `${clayValue} | ${glazeValue}`;
    }
  }

  /**
   * Update the selection display (for hover preview)
   */
  updateSelectionDisplay(clayValue, glazeValue) {
    const claySpan = this.querySelector('.selection-clay');
    const glazeSpan = this.querySelector('.selection-glaze');

    if (!claySpan || !glazeSpan || !clayValue || !glazeValue) return;

    if (this.displayUpdateTimer) {
      clearTimeout(this.displayUpdateTimer);
      this.displayUpdateTimer = null;
    }
    
    const changed = claySpan.textContent !== clayValue || glazeSpan.textContent !== glazeValue;

    if (changed) {
      claySpan.classList.add('updating');
      glazeSpan.classList.add('updating');
      claySpan.textContent = clayValue;
      glazeSpan.textContent = glazeValue;

      this.displayUpdateTimer = setTimeout(() => {
        claySpan.classList.remove('updating');
        glazeSpan.classList.remove('updating');
        this.displayUpdateTimer = null;
      }, 180);
    }
  }

  /**
   * Restore the label to the real selected swatch.
   */
  restoreSelectionDisplay() {
    const selectedLabel = Array.from(this.swatchLabels).find(
      label => label instanceof HTMLElement && label.dataset.selected === 'true'
    );

    if (selectedLabel) {
      this.updateSelectionDisplay(selectedLabel.dataset.clay, selectedLabel.dataset.glaze);
      return;
    }

    if (this.claySelect && this.glazeSelect) {
      this.updateSelectionDisplay(this.claySelect.value, this.glazeSelect.value);
    }
  }

  /**
   * Check if the given values match current selection
   */
  isCurrentSelection(clayValue, glazeValue) {
    if (!this.claySelect || !this.glazeSelect) return false;
    return this.claySelect.value === clayValue && this.glazeSelect.value === glazeValue;
  }

  /**
   * Handle select change (when selects are updated externally)
   */
  handleSelectChange() {
    this.updateActiveSwatch();
  }

  /**
   * Trigger variant change event
   */
  triggerVariantChange(variantId) {
    const variantPicker = this.closest('variant-picker');
    
    // After updating both selects (with events stopped), manually call variantChanged ONCE
    // Use glazeSelect as target since it determines the final variant
    const target = this.glazeSelect || this.claySelect;
    
    if (variantPicker && typeof variantPicker.variantChanged === 'function' && target && variantId) {
      // IMPORTANT: Pass the variant ID directly to variantChanged
      // This is necessary because the hidden select options may not have correct variant IDs
      // for all clay/glaze combinations (they only have one option per glaze value,
      // not per clay+glaze combination). The swatch labels DO have correct variant IDs.
      
      // Call directly with the explicit variant ID - this ensures correct variant selection
      // even when glaze names are shared across clay types
      requestAnimationFrame(() => {
        variantPicker.variantChanged({ 
          target: target,
          // Pass variant ID directly so variantChanged doesn't need to extract it from options
          explicitVariantId: variantId
        });
      });
    } else if (target) {
      // Fallback: trigger one change event on glazeSelect
      requestAnimationFrame(() => {
        const changeEvent = new Event('change', { bubbles: true, cancelable: true });
        target.dispatchEvent(changeEvent);
      });
    }

    // Also dispatch a custom event for other components to listen to
    this.dispatchEvent(new CustomEvent('variant-selected', {
      bubbles: true,
      detail: { variantId: variantId }
    }));
  }

  /**
   * Update URL with variant parameter
   */
  updateUrl(variantId) {
    if (!variantId) return;

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);
    
    if (url.href !== window.location.href) {
      // Use requestYieldCallback to avoid blocking the UI
      if (window.requestYieldCallback) {
        window.requestYieldCallback(() => {
          history.replaceState({}, '', url.toString());
        });
      } else {
        requestAnimationFrame(() => {
          history.replaceState({}, '', url.toString());
        });
      }
    }
  }
}

// Register the custom element
if (!customElements.get('combined-swatch-picker')) {
  customElements.define('combined-swatch-picker', CombinedSwatchPicker);
}
// Auto-initialize existing elements
document.addEventListener('DOMContentLoaded', () => {
  const pickers = document.querySelectorAll('variant-picker');
  pickers.forEach(picker => {
    if (picker.querySelector('.combined-swatch-picker')) {
      // The picker already has our custom HTML, so we don't need to do anything
      // The custom element will initialize automatically
    }
  });
});

